import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, Lock } from "lucide-react";
import type { ReactNode } from "react";
import { AGENT_ACCENTS, AGENT_ICONS } from "@/components/brand/ai-agent-icons";
import { Reveal } from "@/components/reveal";
import { cn } from "@/lib/utils";

type Assistant = {
	key: "claude" | "chatgpt";
	name: string;
	to: string;
	cta: string;
	description: string;
	features: string[];
	/** Faint brand-tinted card border, deepening on hover. */
	borderClass: string;
	/** Subtle brand glow in the top-left corner of the card. */
	glowClass: string;
	/** Brand color for the feature check marks. */
	checkClass: string;
};

const ASSISTANTS: Assistant[] = [
	{
		key: "claude",
		name: "Claude",
		to: "/connect-claude",
		cta: "Connect Claude",
		description: "Best when Claude already helps plan your content.",
		features: [
			"Install with one command",
			"Turn briefs into scheduled posts",
			"Review every post before it goes live",
		],
		borderClass: "border-[#D97757]/25 hover:border-[#D97757]/45",
		glowClass:
			"bg-[radial-gradient(120%_120%_at_0%_0%,rgba(217,119,87,0.07),transparent_55%)]",
		checkClass: "text-[#D97757]",
	},
	{
		key: "chatgpt",
		name: "ChatGPT",
		to: "/connect-chatgpt",
		cta: "Connect ChatGPT",
		description: "Best when your team already works in ChatGPT.",
		features: [
			"Connect from ChatGPT settings",
			"Create posts for every channel",
			"No new tools to learn",
		],
		borderClass: "border-[#10A37F]/25 hover:border-[#10A37F]/45",
		glowClass:
			"bg-[radial-gradient(120%_120%_at_0%_0%,rgba(16,163,127,0.07),transparent_55%)]",
		checkClass: "text-[#10A37F]",
	},
];

function AssistantCard({ assistant }: { assistant: Assistant }) {
	const accent = AGENT_ACCENTS[assistant.key];
	return (
		<div
			className={cn(
				"group relative flex flex-col gap-5 overflow-hidden rounded-lg border bg-background p-5 transition-colors duration-200 sm:p-6",
				assistant.borderClass,
			)}
		>
			<div
				className={cn(
					"pointer-events-none absolute inset-0",
					assistant.glowClass,
				)}
				aria-hidden="true"
			/>

			<div className="relative flex items-center gap-3">
				<span
					className={cn(
						"flex size-9 shrink-0 items-center justify-center rounded-lg",
						accent.bg,
					)}
				>
					{AGENT_ICONS[assistant.key](`size-5 ${accent.text}`)}
				</span>
				<span className="text-lg font-semibold text-foreground">
					{assistant.name}
				</span>
			</div>

			<p className="relative text-sm leading-6 text-muted-foreground">
				{assistant.description}
			</p>

			<ul className="relative grid grid-cols-1 gap-2.5">
				{assistant.features.map((feature) => (
					<li key={feature} className="flex items-start gap-2">
						<Check
							className={cn("mt-0.5 size-4 shrink-0", assistant.checkClass)}
							aria-hidden="true"
						/>
						<span className="text-sm leading-5 text-foreground/80">
							{feature}
						</span>
					</li>
				))}
			</ul>

			<Link
				to={assistant.to}
				className="group/cta relative mt-auto inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
			>
				{assistant.cta}
				<ArrowRight className="size-4 transition-transform group-hover/cta:translate-x-0.5" />
			</Link>
		</div>
	);
}

export function HomepageAgentInstall({
	className,
	id,
}: {
	className?: string;
	id?: string;
}): ReactNode {
	return (
		<section
			id={id}
			className={cn(
				"overflow-hidden rounded-md bg-white dark:bg-black",
				className,
			)}
		>
			<Reveal>
				<div className="px-5 py-10 sm:px-8 sm:py-12">
					<Reveal>
						<h2 className="text-3xl font-semibold tracking-[-0.04em] sm:text-4xl">
							Post from Claude or ChatGPT
						</h2>
						<p
							className="mt-2 max-w-xl text-base text-muted-foreground"
							style={{ lineHeight: "1.6" }}
						>
							Connect AgentReacher once, then ask your assistant to draft,
							schedule, and publish posts across your connected channels.
						</p>
					</Reveal>

					<div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
						{ASSISTANTS.map((assistant, index) => (
							<Reveal
								className="h-full"
								delay={120 + index * 80}
								key={assistant.key}
							>
								<AssistantCard assistant={assistant} />
							</Reveal>
						))}
					</div>

					<Reveal delay={260}>
						<div className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
							<Lock className="size-4 shrink-0" aria-hidden="true" />
							<span>
								Your data is secure. We never post without your permission.
							</span>
						</div>
					</Reveal>
				</div>
			</Reveal>
		</section>
	);
}
